import { useState, useEffect, useRef, useMemo } from "react";

/* ═══════════════════════════════════════════════════════════════════════════
   Mermaid — lazy dynamic import to avoid CRA source-map-loader issues
   ═══════════════════════════════════════════════════════════════════════════ */

let mermaidModule = null;
let mermaidLoadPromise = null;
let mermaidInitializedTheme = null;

const loadMermaid = () => {
  if (mermaidModule) return Promise.resolve(mermaidModule);
  if (!mermaidLoadPromise) {
    mermaidLoadPromise = import(
      /* webpackChunkName: "mermaid" */ "mermaid"
    ).then((mod) => {
      mermaidModule = mod.default || mod;
      return mermaidModule;
    });
  }
  return mermaidLoadPromise;
};

const ensureMermaidInit = (m, dark) => {
  const theme = dark ? "dark" : "neutral";
  if (mermaidInitializedTheme === theme) return;
  m.initialize({
    startOnLoad: false,
    theme,
    securityLevel: "loose",
    fontFamily: "inherit",
    suppressErrorRendering: true,
  });
  mermaidInitializedTheme = theme;
};

let mermaidIdCounter = 0;

const MermaidBlock = ({ children }) => {
  const containerRef = useRef(null);
  const [svg, setSvg] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  /* Extract raw text from whatever ReactMarkdown passes as children.
     It can be a string, array of strings, or nested React elements. */
  const source = useMemo(() => {
    const extractText = (node) => {
      if (typeof node === "string") return node;
      if (typeof node === "number") return String(node);
      if (Array.isArray(node)) return node.map(extractText).join("");
      if (node?.props?.children) return extractText(node.props.children);
      return "";
    };
    let raw = extractText(children);
    /* Decode common HTML entities that rehype may have introduced */
    raw = raw
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'");
    /* Normalize smart/curly quotes to straight quotes — AI models
       frequently output these and mermaid's parser rejects them */
    raw = raw
      .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
      .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
      .replace(/[\u2013\u2014]/g, "-");
    return raw;
  }, [children]);

  useEffect(() => {
    if (!source.trim()) return;
    let cancelled = false;
    setLoading(true);

    const render = async () => {
      try {
        const m = await loadMermaid();
        if (cancelled) return;
        const dark = document.documentElement.classList.contains("dark");
        ensureMermaidInit(m, dark);

        const id = `mermaid-${++mermaidIdCounter}`;

        /* Render into an offscreen container so any error UI stays invisible */
        const offscreen = document.createElement("div");
        offscreen.id = id;
        offscreen.style.cssText =
          "position:fixed;left:-99999px;top:-99999px;width:1px;height:1px;overflow:hidden;";
        document.body.appendChild(offscreen);

        try {
          const { svg: rendered } = await m.render(id, source.trim());
          if (!cancelled) {
            setSvg(rendered);
            setError(false);
          }
        } catch (renderErr) {
          console.error(
            "MERMAID RENDER ERROR:",
            renderErr?.message || renderErr,
          );
          if (!cancelled) setError(true);
        } finally {
          offscreen.remove();
          const stray = document.getElementById(`d${id}`);
          if (stray) stray.remove();
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
        /* Final sweep: remove any mermaid error SVGs that leaked into the page */
        requestAnimationFrame(() => {
          document.querySelectorAll("svg").forEach((el) => {
            if (
              el.textContent &&
              el.textContent.includes("Syntax error in text") &&
              !el.closest("[data-mermaid-ok]")
            ) {
              const parent = el.parentElement;
              if (parent && parent !== document.body) parent.remove();
              else el.remove();
            }
          });
        });
      }
    };

    render();
    return () => {
      cancelled = true;
    };
  }, [source]);

  if (loading || error || !svg) {
    return (
      <div className="my-3 rounded-lg overflow-hidden bg-[#1e1e2e] border border-zinc-700">
        <div className="flex items-center justify-between px-4 py-2 bg-[#181825] border-b border-zinc-700">
          <span className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider">
            {error
              ? "mermaid (parse error)"
              : loading
                ? "mermaid (loading…)"
                : "mermaid"}
          </span>
        </div>
        <pre className="overflow-x-auto p-4">
          <code className="text-[13px] leading-relaxed text-zinc-200 font-mono">
            {source}
          </code>
        </pre>
      </div>
    );
  }

  return (
    <div className="my-4 flex justify-center" data-mermaid-ok>
      <div
        ref={containerRef}
        className="overflow-x-auto max-w-full [&_svg]:max-w-full [&_svg]:h-auto"
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    </div>
  );
};

export default MermaidBlock;
